import { audioEngine } from './AudioEngine';

/**
 * Music.ts — the adaptive generative soundtrack, M14 rework (owner: "less
 * video-gamey, more soccer/futbol, with the musical sounds of each city's
 * culture"). Still 100% synthesized and swappable for licensed tracks later.
 *
 * Each city is a STYLE: a culture-true drum pattern (samba surdo+tamborim for
 * Rio, tarantella 6/8 for Napoli, mbalax-ish sabar for Dakar, afrobeat groove
 * for Lagos, garage shuffle for London, tabla+tanpura drone for Mumbai, taiko
 * + koto for Tokyo, Andean bombo+quena for Patagonia…), a scale, a bass idea,
 * and a melodic INSTRUMENT recipe (marimba / koto / mandolin / kora / quena /
 * organ stabs) instead of the old square-wave arp. Under every race mood sits
 * a soft STADIUM CROWD bed — distant terrace swells, the futbol glue.
 *
 * Engine: a tiny lookahead step sequencer; bars are 16 steps (4/4) or 12
 * steps (6/8 feel), with optional swing on the off-steps.
 *
 * M45 "sound better" pass (owner ask) — same grooves, real PRODUCTION:
 *   - a synthesized-IR reverb SEND (leads/pads wet, kick/bass nearly dry) +
 *     a gentle bus compressor with makeup gain — glue and depth, zero assets;
 *   - STEREO: every voice sits somewhere (hats right, shaker left, agogo
 *     split, leads drift per-note) instead of a mono centre pile;
 *   - beat ACCENTS (downbeats lean in, off-steps sit back), a bar-4 lead lift
 *     + a tiny percussion turnaround so the 4-bar loop breathes;
 *   - punchier kick (click transient), natural exponential noise decays,
 *     and re-entering a menu no longer restarts the groove mid-bar.
 */

type Perc =
  | 'kick' // round club kick
  | 'surdo' // deep samba drum on the 2
  | 'taiko' // big festival drum
  | 'bombo' // soft Andean thump
  | 'snare' // tight noise snare
  | 'rim' // woody click (clave / tamborim accent)
  | 'agogoHi'
  | 'agogoLo'
  | 'shaker'
  | 'hat'
  | 'tablaDha' // low tabla stroke (pitch-bent)
  | 'tablaNa'; // bright tabla tick

type Lead = 'marimba' | 'koto' | 'mandolin' | 'kora' | 'quena' | 'organ' | 'brass';

interface Style {
  bpm: number;
  root: number; // MIDI key centre
  scale: number[]; // semitone offsets — the city's mode
  steps: 12 | 16; // steps per bar: 16 = 4/4, 12 = 6/8 feel
  swing?: number; // 0..1 — delays the off-steps (garage/funk shuffle)
  drums: Partial<Record<Perc, number[]>>; // step indices per percussion voice
  /** Bass notes as [step, scaleDegree] pairs (degree into `scale`, -1 octave). */
  bass: [number, number][];
  /** Sustained drone instead of a bassline (tanpura-style root + fifth). */
  drone?: boolean;
  lead: { voice: Lead; density: number; octave: number };
  pad?: boolean;
  crowd: number; // stadium-bed gain (0 = none)
}

const STYLES: Record<string, Style> = {
  // Menu — fun, global, easy on the ears: warm marimba over a lazy world groove.
  menu: {
    bpm: 96,
    root: 57,
    scale: [0, 2, 4, 7, 9],
    steps: 16,
    drums: { kick: [0, 10], shaker: [2, 6, 10, 14], rim: [4, 12] },
    bass: [
      [0, 0],
      [8, 3],
    ],
    lead: { voice: 'marimba', density: 0.5, octave: 1 },
    pad: true,
    crowd: 0.5,
  },
  // NYC — block-party funk: swung hats, brass stabs.
  nyc: {
    bpm: 112,
    root: 57,
    scale: [0, 2, 4, 7, 9],
    steps: 16,
    swing: 0.18,
    drums: { kick: [0, 7, 10], snare: [4, 12], hat: [2, 6, 8, 14], shaker: [1, 5, 9, 13] },
    bass: [
      [0, 0],
      [7, 4],
      [10, 3],
    ],
    lead: { voice: 'brass', density: 0.35, octave: 0 },
    crowd: 1,
  },
  // London — 2-step garage shuffle, moody minor.
  london: {
    bpm: 124,
    root: 55,
    scale: [0, 3, 5, 7, 10],
    steps: 16,
    swing: 0.32,
    drums: { kick: [0, 10], snare: [4, 12], hat: [2, 6, 9, 14], rim: [7, 15] },
    bass: [
      [0, 0],
      [7, 2],
      [11, 1],
    ],
    lead: { voice: 'organ', density: 0.3, octave: 0 },
    crowd: 1,
  },
  // Napoli — tarantella: 6/8 lilt, mandolin tremolo, sunny major.
  napoli: {
    bpm: 150,
    root: 60,
    scale: [0, 2, 4, 5, 7, 9],
    steps: 12,
    drums: { kick: [0, 6], rim: [3, 9], shaker: [2, 5, 8, 11] },
    bass: [
      [0, 0],
      [6, 4],
    ],
    lead: { voice: 'mandolin', density: 0.66, octave: 1 },
    crowd: 1,
  },
  // Tokyo — night drive: taiko booms, koto plucks on the in-scale, space.
  tokyo: {
    bpm: 100,
    root: 52,
    scale: [0, 3, 5, 7, 10],
    steps: 16,
    drums: { taiko: [0, 11], hat: [4, 8, 12], rim: [6, 14] },
    bass: [
      [0, 0],
      [11, 2],
    ],
    lead: { voice: 'koto', density: 0.4, octave: 1 },
    pad: true,
    crowd: 0.7,
  },
  // Rio — samba: surdo on the 2, tamborim chatter, agogo two-tone.
  rio: {
    bpm: 104,
    root: 62,
    scale: [0, 2, 4, 7, 9],
    steps: 16,
    drums: {
      surdo: [4, 12],
      kick: [0],
      rim: [0, 3, 5, 8, 11, 14], // tamborim pattern
      agogoHi: [2, 10],
      agogoLo: [6, 15],
      shaker: [1, 3, 5, 7, 9, 11, 13, 15],
    },
    bass: [
      [0, 0],
      [8, 4],
    ],
    lead: { voice: 'marimba', density: 0.45, octave: 1 },
    crowd: 1.4, // the Maracanã is IN the music
  },
  // Dakar — mbalax-leaning: busy sabar syncopation over a kora arpeggio.
  dakar: {
    bpm: 120,
    root: 53,
    scale: [0, 2, 4, 7, 9],
    steps: 12,
    drums: { kick: [0, 5, 8], rim: [2, 4, 7, 10], shaker: [1, 3, 6, 9, 11], snare: [6] },
    bass: [
      [0, 0],
      [5, 3],
      [8, 4],
    ],
    lead: { voice: 'kora', density: 0.6, octave: 1 },
    crowd: 1,
  },
  // Lagos — afrobeat: tight hats, off-beat round bass, organ stabs.
  lagos: {
    bpm: 110,
    root: 58,
    scale: [0, 2, 4, 7, 9],
    steps: 16,
    drums: {
      kick: [0, 6, 10],
      rim: [3, 7, 12], // 3-2-ish clave
      hat: [0, 2, 4, 6, 8, 10, 12, 14],
      shaker: [1, 5, 9, 13],
    },
    bass: [
      [0, 0],
      [3, 0],
      [6, 4],
      [10, 3],
      [12, 0],
    ],
    lead: { voice: 'organ', density: 0.4, octave: 0 },
    crowd: 1.2,
  },
  // Mumbai — tabla over a tanpura drone, raga-leaning line, no bassline.
  mumbai: {
    bpm: 96,
    root: 55,
    scale: [0, 1, 4, 5, 7, 8, 11],
    steps: 16,
    drums: { tablaDha: [0, 7, 10], tablaNa: [2, 4, 8, 12, 14], shaker: [6] },
    bass: [],
    drone: true,
    lead: { voice: 'koto', density: 0.45, octave: 1 }, // sitar-adjacent pluck
    crowd: 1,
  },
  // Patagonia — Andean: bombo heartbeat, quena flute over charango-ish plucks.
  patagonia: {
    bpm: 88,
    root: 50,
    scale: [0, 3, 5, 7, 10],
    steps: 12,
    drums: { bombo: [0, 7], shaker: [3, 9], rim: [5] },
    bass: [
      [0, 0],
      [7, 3],
    ],
    lead: { voice: 'quena', density: 0.3, octave: 1 },
    pad: true,
    crowd: 0.4, // the wind more than the terraces
  },
};

const midiHz = (n: number): number => 440 * Math.pow(2, (n - 69) / 12);

/** Where a one-shot voice sits and how much room it gets. */
interface VoiceOpts {
  lp?: number; // lowpass cutoff (Hz)
  pan?: number; // -1..1 stereo position
  send?: number; // 0..1 reverb send amount
}

/** Stereo seats per percussion voice — a mixed kit, not a mono pile. */
const PERC_PAN: Record<Perc, number> = {
  kick: 0,
  surdo: 0,
  taiko: 0,
  bombo: 0,
  snare: 0.06,
  rim: 0.16,
  agogoHi: 0.35,
  agogoLo: -0.3,
  shaker: -0.28,
  hat: 0.3,
  tablaDha: -0.12,
  tablaNa: 0.14,
};

/** Reverb send per percussion voice (kick/bass stay dry — mud avoidance). */
const PERC_SEND: Record<Perc, number> = {
  kick: 0.04,
  surdo: 0.06,
  taiko: 0.3,
  bombo: 0.12,
  snare: 0.24,
  rim: 0.18,
  agogoHi: 0.2,
  agogoLo: 0.2,
  shaker: 0.08,
  hat: 0.08,
  tablaDha: 0.14,
  tablaNa: 0.16,
};

export class Music {
  private style: Style | null = null;
  private timer: number | null = null;
  private nextTime = 0;
  private step = 0;
  private crowdNodes: { src: AudioBufferSourceNode; gain: GainNode; lfo: OscillatorNode } | null =
    null;
  private nextSwell = 0;
  /** The production chain (M45): voices → in → compressor → makeup → musicBus,
   *  with a parallel reverb send (in is per-note via panners; see voice()). */
  private fxCtx: AudioContext | null = null;
  private fxIn: GainNode | null = null;
  private fxVerbIn: GainNode | null = null;

  /** Start (or switch to) a city's style; unknown ids fall back to the menu. */
  play(cityId: string): void {
    const style = STYLES[cityId] ?? STYLES.menu;
    const ctx = audioEngine.context;
    // Same mood already grooving (menu → settings → menu…) — don't restart the
    // bar. Only re-enter when the style, or the audio context, actually changed.
    if (ctx && this.style === style && this.timer !== null) return;
    this.style = style;
    this.step = 0;
    if (!ctx) return; // unlock() hasn't happened yet — play() again later
    this.nextTime = ctx.currentTime + 0.1;
    this.startCrowd(style);
    if (this.timer === null) {
      this.timer = window.setInterval(() => this.pump(), 90);
    }
  }

  stop(): void {
    this.style = null;
    this.stopCrowd();
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  // ----- The production chain (M45) -------------------------------------------

  /** Voices feed `fxIn` (glued by a gentle compressor + makeup into the music
   *  bus) and, per-note, a reverb send into a synthesized-IR convolver. Built
   *  once per AudioContext. */
  private ensureFx(ctx: AudioContext, bus: GainNode): GainNode {
    if (this.fxCtx === ctx && this.fxIn) return this.fxIn;
    const input = ctx.createGain();
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -16;
    comp.knee.value = 10;
    comp.ratio.value = 3.5;
    comp.attack.value = 0.005;
    comp.release.value = 0.2;
    const makeup = ctx.createGain();
    makeup.gain.value = 1.15;
    input.connect(comp).connect(makeup).connect(bus);

    const verb = ctx.createConvolver();
    verb.buffer = this.makeImpulse(ctx);
    const wet = ctx.createGain();
    wet.gain.value = 0.75;
    const verbIn = ctx.createGain();
    verbIn.connect(verb).connect(wet).connect(comp);

    this.fxCtx = ctx;
    this.fxIn = input;
    this.fxVerbIn = verbIn;
    return input;
  }

  /** A small dark hall: 1.6 s of exponentially-decaying smoothed noise, stereo
   *  (decorrelated channels). Synthesized — still zero audio assets. */
  private makeImpulse(ctx: AudioContext): AudioBuffer {
    const dur = 1.6;
    const len = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const data = buf.getChannelData(ch);
      let smooth = 0;
      for (let i = 0; i < len; i++) {
        // One-pole smoothing darkens the tail (a bright IR reads as hiss).
        smooth = smooth * 0.62 + (Math.random() * 2 - 1) * 0.38;
        data[i] = smooth * Math.exp(-i / (ctx.sampleRate * 0.42));
      }
    }
    return buf;
  }

  // ----- The stadium-crowd bed (the futbol glue under every race mood) -------

  private startCrowd(style: Style): void {
    this.stopCrowd();
    const ctx = audioEngine.context;
    const bus = audioEngine.musicBus;
    if (!ctx || !bus || style.crowd <= 0) return;
    // A 2s brown-noise loop through a band-pass ≈ a distant terrace murmur.
    const len = Math.floor(ctx.sampleRate * 2);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      last = (last + (Math.random() * 2 - 1) * 0.02) * 0.985;
      data[i] = last * 18;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 650;
    bp.Q.value = 0.6;
    const gain = ctx.createGain();
    gain.gain.value = 0.028 * style.crowd;
    // A slow LFO breathes the murmur like a real crowd.
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.13;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.012 * style.crowd;
    lfo.connect(lfoGain).connect(gain.gain);
    // The crowd stays OFF the compressor (it would pump under the kick).
    src.connect(bp).connect(gain).connect(bus);
    src.start();
    lfo.start();
    this.crowdNodes = { src, gain, lfo };
    this.nextSwell = ctx.currentTime + 6;
  }

  private stopCrowd(): void {
    if (!this.crowdNodes) return;
    try {
      this.crowdNodes.src.stop();
      this.crowdNodes.lfo.stop();
    } catch {
      /* already stopped */
    }
    this.crowdNodes = null;
  }

  /** An occasional terrace swell — something just happened down the far end. */
  private maybeSwell(ctx: AudioContext): void {
    if (!this.crowdNodes || !this.style) return;
    if (ctx.currentTime < this.nextSwell) return;
    this.nextSwell = ctx.currentTime + 7 + Math.random() * 9;
    const g = this.crowdNodes.gain.gain;
    const base = 0.028 * this.style.crowd;
    const t = ctx.currentTime;
    g.cancelScheduledValues(t);
    g.setValueAtTime(base, t);
    g.linearRampToValueAtTime(base * (2.2 + Math.random()), t + 0.7);
    g.linearRampToValueAtTime(base, t + 2.4);
  }

  // ----- The sequencer --------------------------------------------------------

  /** Schedule every due step ~0.25s ahead (the classic lookahead pattern). */
  private pump(): void {
    const ctx = audioEngine.context;
    const bus = audioEngine.musicBus;
    if (!ctx || !bus || !this.style) return;
    this.ensureFx(ctx, bus);
    const s = this.style;
    const stepDur = 60 / s.bpm / (s.steps / 4);
    while (this.nextTime < ctx.currentTime + 0.25) {
      const inBar = this.step % s.steps;
      const swing = s.swing && inBar % 2 === 1 ? s.swing * stepDur * 0.4 : 0;
      this.schedule(ctx, s, this.step, this.nextTime + swing, stepDur);
      this.nextTime += stepDur;
      this.step = (this.step + 1) % (s.steps * 4); // 4-bar loop
    }
    this.maybeSwell(ctx);
  }

  /** Downbeats lean in, off-steps sit back — the difference between a grid
   *  and a groove. Quarter-beats get a light lift (6/8 styles: the two "big"
   *  beats live at 0 and steps/2). */
  private accent(s: Style, beat: number): number {
    if (beat === 0) return 1.16;
    const quarter = s.steps === 12 ? 3 : 4;
    if (beat % quarter === 0) return 1.05;
    return beat % 2 === 1 ? 0.84 : 1;
  }

  private schedule(ctx: AudioContext, s: Style, step: number, t: number, stepDur: number): void {
    const bar = Math.floor(step / s.steps) % 4;
    const beat = step % s.steps;
    const vel = this.accent(s, beat);

    // Drums.
    for (const [voice, steps] of Object.entries(s.drums) as [Perc, number[]][]) {
      if (steps.includes(beat)) this.perc(ctx, voice, t, vel);
    }
    // A tiny turnaround into the next loop: one extra tick on the last
    // off-step of bar 4 (shaker if the kit has one, else the rim).
    if (bar === 3 && beat === s.steps - 1) {
      const turn: Perc | null = s.drums.shaker ? 'shaker' : s.drums.rim ? 'rim' : null;
      if (turn && !s.drums[turn]?.includes(beat)) this.perc(ctx, turn, t, 0.9);
    }

    // Bass (degree into the scale, an octave down; bar IV lifts a fourth).
    for (const [bStep, degree] of s.bass) {
      if (beat === bStep) {
        const lift = bar === 3 ? 5 : 0;
        const note = s.root - 12 + s.scale[degree % s.scale.length] + lift;
        // A saw through a closing filter (round funk) + a clean sine sub.
        this.osc(ctx, 'sawtooth', midiHz(note), midiHz(note), t, stepDur * 2.6, 0.13 * vel, {
          lp: 420,
          send: 0.05,
        });
        this.osc(ctx, 'sine', midiHz(note - 12), midiHz(note - 12), t, stepDur * 2.6, 0.1 * vel);
      }
    }

    // Tanpura-style drone (Mumbai): root + fifth, re-bowed every bar.
    if (s.drone && beat === 0) {
      const barDur = stepDur * s.steps;
      for (const semi of [0, 7]) {
        const hz = midiHz(s.root - 12 + semi);
        this.osc(ctx, 'sawtooth', hz, hz * 1.002, t, barDur * 1.05, 0.045, {
          lp: 700,
          send: 0.2,
          pan: semi === 0 ? -0.08 : 0.08,
        });
      }
    }

    // Pad: a soft sustained triad every 2 bars (where the style wants air),
    // the voices fanned across the stereo field and well into the hall.
    if (s.pad && beat === 0 && bar % 2 === 0) {
      const spread = [-0.25, 0.1, 0.3];
      [0, s.scale[2] ?? 4, 7].forEach((semi, i) => {
        const hz = midiHz(s.root + semi);
        this.osc(ctx, 'triangle', hz, hz, t, stepDur * s.steps * 1.9, 0.04, {
          lp: 1000,
          send: 0.5,
          pan: spread[i],
        });
      });
    }

    // The lead instrument — a deterministic walk on the city's scale. Bar 4
    // plays a touch denser (the turnaround), and each note drifts a little in
    // the stereo field.
    const lead = s.lead;
    const density = lead.density * (bar === 3 ? 1.25 : 1);
    const hit = (step * 5 + bar * 3) % 16;
    if (hit / 16 < density && beat % 2 === 0) {
      const idx = (step * 3 + bar * 2) % s.scale.length;
      const note = s.root + 12 * lead.octave + s.scale[idx];
      const pan = (((step * 7) % 5) - 2) * 0.09;
      this.leadNote(ctx, lead.voice, midiHz(note), t, stepDur, vel, pan);
    }
  }

  // ----- Instrument recipes ---------------------------------------------------

  private leadNote(
    ctx: AudioContext,
    voice: Lead,
    hz: number,
    t: number,
    stepDur: number,
    vel: number,
    pan: number,
  ): void {
    switch (voice) {
      case 'marimba': // warm wooden bars: sine + a fifth overtone, quick decay
        this.osc(ctx, 'sine', hz, hz, t, 0.5, 0.085 * vel, { pan, send: 0.3 });
        this.osc(ctx, 'sine', hz * 3, hz * 3, t, 0.12, 0.02 * vel, { pan, send: 0.3 });
        break;
      case 'koto': // sharp pluck with a tiny pitch settle
        this.osc(ctx, 'triangle', hz * 1.012, hz, t, 0.45, 0.08 * vel, {
          lp: 1600,
          pan,
          send: 0.35,
        });
        break;
      case 'mandolin': // tremolo double-strike, the pair split slightly
        this.osc(ctx, 'triangle', hz, hz, t, 0.16, 0.07 * vel, { lp: 2200, pan, send: 0.25 });
        this.osc(ctx, 'triangle', hz * 1.003, hz, t + 0.055, 0.16, 0.055 * vel, {
          lp: 2200,
          pan: pan + 0.08,
          send: 0.25,
        });
        break;
      case 'kora': // soft harp, long ring, deep in the hall
        this.osc(ctx, 'triangle', hz, hz, t, 0.9, 0.06 * vel, { lp: 1400, pan, send: 0.4 });
        this.osc(ctx, 'sine', hz * 2, hz * 2, t, 0.4, 0.018 * vel, { pan, send: 0.4 });
        break;
      case 'quena': // breathy Andean flute: slow attack + vibrato
        this.fluteNote(ctx, hz, t, stepDur * 5, vel, pan);
        break;
      case 'organ': // short two-note stab
        this.osc(ctx, 'triangle', hz, hz, t, 0.22, 0.06 * vel, { lp: 1200, pan, send: 0.2 });
        this.osc(ctx, 'triangle', hz * 1.5, hz * 1.5, t, 0.22, 0.04 * vel, {
          lp: 1200,
          pan,
          send: 0.2,
        });
        break;
      case 'brass': // filtered saw stab
        this.osc(ctx, 'sawtooth', hz, hz, t, 0.2, 0.07 * vel, { lp: 900, pan, send: 0.25 });
        break;
    }
  }

  private fluteNote(
    ctx: AudioContext,
    hz: number,
    t: number,
    dur: number,
    vel: number,
    pan: number,
  ): void {
    const sink = this.fxIn;
    if (!sink) return;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = hz;
    const vib = ctx.createOscillator();
    vib.frequency.value = 5.2;
    const vibGain = ctx.createGain();
    vibGain.gain.value = hz * 0.012;
    vib.connect(vibGain).connect(osc.frequency);
    const g = ctx.createGain();
    const peak = 0.075 * vel;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.1);
    g.gain.setValueAtTime(peak, t + dur * 0.7);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    const p = ctx.createStereoPanner();
    p.pan.value = pan;
    osc.connect(g).connect(p).connect(sink);
    this.sendToVerb(ctx, p, 0.45);
    osc.start(t);
    vib.start(t);
    osc.stop(t + dur + 0.05);
    vib.stop(t + dur + 0.05);
  }

  private perc(ctx: AudioContext, voice: Perc, t: number, vel: number): void {
    const pan = PERC_PAN[voice];
    const send = PERC_SEND[voice];
    switch (voice) {
      case 'kick': // round body + a tiny click transient for punch
        this.osc(ctx, 'sine', 120, 44, t, 0.16, 0.42 * vel, { pan, send });
        this.noise(ctx, t, 0.012, 0.07 * vel, 3200, { pan, send: 0 });
        break;
      case 'surdo': // the deep samba heartbeat
        this.osc(ctx, 'sine', 80, 46, t, 0.34, 0.5 * vel, { pan, send });
        break;
      case 'taiko':
        this.osc(ctx, 'sine', 95, 40, t, 0.42, 0.5 * vel, { pan, send });
        this.noise(ctx, t, 0.05, 0.08 * vel, 900, { pan, send });
        break;
      case 'bombo': // softer, woollier
        this.osc(ctx, 'sine', 70, 42, t, 0.3, 0.34 * vel, { pan, send });
        break;
      case 'snare':
        this.noise(ctx, t, 0.11, 0.12 * vel, 1700, { pan, send });
        this.osc(ctx, 'triangle', 190, 150, t, 0.07, 0.06 * vel, { pan, send: 0.1 });
        break;
      case 'rim':
        this.osc(ctx, 'square', 1750, 1500, t, 0.03, 0.045 * vel, { pan, send });
        break;
      case 'agogoHi':
        this.osc(ctx, 'square', 880, 860, t, 0.09, 0.05 * vel, { pan, send });
        break;
      case 'agogoLo':
        this.osc(ctx, 'square', 587, 575, t, 0.1, 0.05 * vel, { pan, send });
        break;
      case 'shaker':
        this.noise(ctx, t, 0.035, 0.035 * vel, 6800, { pan, send });
        break;
      case 'hat':
        this.noise(ctx, t, 0.03, 0.05 * vel, 7800, { pan, send });
        break;
      case 'tablaDha': // resonant low stroke, pitch falling
        this.osc(ctx, 'sine', 150, 88, t, 0.22, 0.3 * vel, { pan, send });
        this.noise(ctx, t, 0.02, 0.04 * vel, 2500, { pan, send });
        break;
      case 'tablaNa':
        this.noise(ctx, t, 0.03, 0.05 * vel, 3000, { pan, send });
        this.osc(ctx, 'sine', 480, 470, t, 0.05, 0.04 * vel, { pan, send });
        break;
    }
  }

  // ----- Primitive voices -----------------------------------------------------

  /** Route a per-note chain tail into the dry input + the reverb send. */
  private route(ctx: AudioContext, tail: AudioNode, pan: number, send: number): void {
    const sink = this.fxIn;
    if (!sink) return;
    const p = ctx.createStereoPanner();
    p.pan.value = pan;
    tail.connect(p).connect(sink);
    this.sendToVerb(ctx, p, send);
  }

  private sendToVerb(ctx: AudioContext, from: AudioNode, amount: number): void {
    if (!this.fxVerbIn || amount <= 0) return;
    const sg = ctx.createGain();
    sg.gain.value = amount;
    from.connect(sg).connect(this.fxVerbIn);
  }

  private osc(
    ctx: AudioContext,
    type: OscillatorType,
    f0: number,
    f1: number,
    t: number,
    dur: number,
    gain: number,
    o: VoiceOpts = {},
  ): void {
    if (!this.fxIn) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f0, t);
    if (f1 !== f0) osc.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + Math.min(0.04, dur * 0.2));
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    if (o.lp) {
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = o.lp;
      osc.connect(f).connect(g);
    } else {
      osc.connect(g);
    }
    this.route(ctx, g, o.pan ?? 0, o.send ?? 0.1);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  private noise(
    ctx: AudioContext,
    t: number,
    dur: number,
    gain: number,
    hp: number,
    o: VoiceOpts = {},
  ): void {
    if (!this.fxIn) return;
    const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    // Quadratic decay reads as a natural percussive tail (linear reads gated).
    for (let i = 0; i < len; i++) {
      const env = 1 - i / len;
      data[i] = (Math.random() * 2 - 1) * env * env;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const f = ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.value = hp;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    src.connect(f).connect(g);
    this.route(ctx, g, o.pan ?? 0, o.send ?? 0.1);
    src.start(t);
  }
}

export const music = new Music();
